/**
 * CartPricingSummary — sticky footer panel for the cart flyout.
 */
import {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';

import {Component as ContentfulComponent} from '@amblique/contentful/app/components/contentful/component';
import {LockIcon} from '@amblique/king-living/app/components/icons';
import {useShippingMethods} from '@amblique/king-living/app/hooks/use-shipping-methods';
import {useCartShippingEstimateAddress} from '@amblique/king-living/app/hooks/use-cart-shipping-estimate-address';
import {useGlobalConfig} from '@amblique/king-living/app/contexts/global-config-context';
import {useCartFlyoutContext} from '@amblique/king-living/app/contexts/cart-flyout-context';
import BuyNowPayLaterMessaging, {BNPL_PAGE_TYPE} from '@amblique/king-living/app/components/buy-now-pay-later';
import {
  getBasketTaxLabel,
  getBasketTaxNotCalculatedLabel,
  hasBasketShippingAddress,
  resolveBasketPricingSummaryTotals,
} from '@amblique/king-living/app/utils/basket-tax-utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {isPickupMethod} from '@amblique/king-living/app/utils/shipping-method-utils';

const SHIPPING_SUMMARY_MESSAGES = {
  estimatedShippingLabel: {
    defaultMessage: 'Estimated shipping',
    id: 'cart_pricing_summary.shipping_label',
  },
  shippingFrom: {
    defaultMessage: 'From {price}',
    id: 'cart_pricing_summary.shipping_from',
  },
  shippingLabel: {
    defaultMessage: 'Shipping',
    id: 'order_summary.label.shipping',
  },
};

// ---------------------------------------------------------------------------
// PriceRow sub-component
// ---------------------------------------------------------------------------

const PriceRow = ({isDiscount, isTotal, label, styles, value}) => (
  <Flex className="cart-pricing-summary__price-row" {...styles.priceRow}>
    <Text
      className="cart-pricing-summary__price-row-label"
      {...styles.priceRowLabel}
      {...(isDiscount ? {color: 'secondary.newSaleRed'} : {})}
      {...(isTotal ?
        {
          color: 'greyscale.100',
          fontSize: '20px',
          fontWeight: 'semibold',
        } :
        {})}
    >
      {label}
    </Text>
    <Text
      className="cart-pricing-summary__price-row-value"
      {...styles.priceRowValue}
      {...(isDiscount ? {color: 'secondary.newSaleRed'} : {})}
      {...(isTotal ?
        {
          color: 'greyscale.100',
          fontSize: '20px',
          fontWeight: 'semibold',
        } :
        {})}
    >
      {value}
    </Text>
  </Flex>
);

PriceRow.displayName = 'PriceRow';
PriceRow.propTypes = {
  isDiscount: PropTypes.bool,
  isTotal: PropTypes.bool,
  label: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
};

const CartPricingSummary = ({totalSavings = 0}) => {
  const intl = useIntl();
  const navigate = useNavigation();
  const {site} = useMultiSite();
  const styles = useMultiStyleConfig('CartFlyout', {});
  const {isOpen, onClose} = useCartFlyoutContext();
  const {data: basket} = useCurrentBasket();
  const {cartPaymentIcons} = useGlobalConfig();
  const currency = basket?.currency ?? 'AUD';
  const basketHasShippingAddress = hasBasketShippingAddress(basket);

  const {estimateAddress, isResolving: isGeoResolving} = useCartShippingEstimateAddress({
    enabled: isOpen && Boolean(basket?.basketId),
    basket,
    siteId: site?.id,
    geoApiKey: site?.preferences?.GOOGLE_GEOLOCATION_BROWSER_API_KEY,
  });

  const {data: shippingData, isLoading: isShippingLoading} = useShippingMethods({
    cacheContext: 'checkout',
    enabled: Boolean(basket?.basketId),
    estimateAddress,
  });

  const formatCurrency = useCallback(
    (amount) => {
      if (typeof amount !== 'number') return null;

      return formatCurrencyHideTrailingZeroCents(intl, amount, currency);
    },
    [intl, currency]
  );

  const hasSelectedShipping = Boolean(basket?.shipments?.[0]?.shippingMethod?.id);

  const minShippingPrice = useMemo(() => {
    if (hasSelectedShipping) return null;

    const methods = (shippingData?.applicableShippingMethods ?? []).filter(
      (m) => !isPickupMethod(m)
    );

    if (!methods.length) return null;

    return Math.min(...methods.map((m) => m.actualPrice ?? m.price ?? 0));
  }, [hasSelectedShipping, shippingData]);

  // Subtotal = list-price total (RRP × qty), Total savings = single rolled-up
  // deduction sourced from `basket.c_savingTotal`, Total price = SCAPI's
  // `basket.orderTotal` (with a `productTotal + shippingTotal` fallback).
  //
  // Intentionally diverges from canonical PWA-Kit `OrderSummary` (which uses
  // `productSubTotal` / `orderTotal || productTotal`). Switching to canonical
  // without also redesigning the savings row breaks the math whenever catalog
  // savings exist. Keep aligned with `order-summary/checkout-order-summary.jsx`.
  //
  // Full rationale & rules for future changes:
  //   docs/checkout/pricing-summary-design.md
  const subtotal = basket?.productTotal != null ? (basket.productTotal + totalSavings) : null;

  const pricingTotals = useMemo(
    () => resolveBasketPricingSummaryTotals({
      basket,
      estimatedShipping: minShippingPrice,
      hasSelectedShipping,
      siteId: site?.id,
    }),
    [basket, hasSelectedShipping, minShippingPrice, site?.id]
  );

  const {orderTotal, taxDisplay} = pricingTotals;

  const taxLabel = useMemo(() => getBasketTaxLabel(intl, basket), [intl, basket]);

  const taxValue = useMemo(() => {
    if (taxDisplay.kind === 'amount') {
      return formatCurrency(taxDisplay.amount);
    }

    if (taxDisplay.kind === 'placeholder') {
      return getBasketTaxNotCalculatedLabel(intl);
    }

    return null;
  }, [taxDisplay, intl, formatCurrency]);

  const handleCheckout = useCallback(() => {
    onClose();
    navigate('/checkout');
  }, [onClose, navigate]);

  const shippingLabel = useMemo(() => {
    if (hasSelectedShipping) {
      return intl.formatMessage(SHIPPING_SUMMARY_MESSAGES.shippingLabel);
    }

    return intl.formatMessage(SHIPPING_SUMMARY_MESSAGES.estimatedShippingLabel);
  }, [hasSelectedShipping, intl]);

  const shippingValue = useMemo(() => {
    if (hasSelectedShipping) {
      const formattedPrice = formatCurrency(basket?.shippingTotal ?? 0);

      return formattedPrice;
    }

    if (minShippingPrice !== null) {
      const formattedPrice = formatCurrency(minShippingPrice);

      return intl.formatMessage(SHIPPING_SUMMARY_MESSAGES.shippingFrom, {price: formattedPrice});
    }

    return null;
  }, [basket?.shippingTotal, formatCurrency, hasSelectedShipping, intl, minShippingPrice]);

  const showShippingLoading =
    isShippingLoading || (!hasSelectedShipping && !basketHasShippingAddress && isGeoResolving);

  return (
    <Stack className="cart-pricing-summary__root" {...styles.pricingSummary}>
      <Stack className="cart-pricing-summary__pricing-block" spacing={4} width="100%">
        <Stack className="cart-pricing-summary__pricing-rows" {...styles.pricingRows}>
          {subtotal != null && (
            <PriceRow
              styles={styles}
              value={formatCurrency(subtotal)}
              label={intl.formatMessage({
                defaultMessage: 'Subtotal',
                id: 'cart_pricing_summary.subtotal',
              })}
            />
          )}

          {showShippingLoading ? (
            <Flex align="center" className="cart-pricing-summary__shipping-loading" gap={2}>
              <Text {...styles.priceRowLabel}>{shippingLabel}</Text>
              <Spinner size="xs" />
            </Flex>
          ) : (
            shippingValue != null && (
              <PriceRow label={shippingLabel} styles={styles} value={shippingValue} />
            )
          )}

          {totalSavings > 0 && (
            <PriceRow
              styles={styles}
              value={`−${formatCurrency(totalSavings)}`}
              label={intl.formatMessage({
                defaultMessage: 'Total savings',
                id: 'cart_pricing_summary.total_savings',
              })}
              isDiscount
            />
          )}

          {taxValue != null && (
            <PriceRow label={taxLabel} styles={styles} value={taxValue} />
          )}
        </Stack>

        <Stack className="cart-pricing-summary__total-block" {...styles.totalBlock}>
          <Box className="cart-pricing-summary__divider" {...styles.pricingDivider} />
          <PriceRow
            styles={styles}
            value={formatCurrency(orderTotal)}
            label={intl.formatMessage({
              defaultMessage: 'Total price',
              id: 'cart_pricing_summary.total_price',
            })}
            isTotal
          />
        </Stack>

        <BuyNowPayLaterMessaging
          amount={orderTotal}
          currency={currency}
          pageType={BNPL_PAGE_TYPE.CART}
        />
      </Stack>

      <Button
        className="cart-pricing-summary__checkout-btn"
        size="checkout"
        type="button"
        variant="unstyled"
        {...styles.checkoutButton}
        aria-label={intl.formatMessage({
          defaultMessage: 'Proceed to checkout',
          id: 'cart_pricing_summary.checkout_aria',
        })}
        onClick={handleCheckout}
      >
        <LockIcon boxSize="20px" color="currentColor" flexShrink={0} />
        {intl.formatMessage({defaultMessage: 'Secure checkout', id: 'cart_pricing_summary.checkout'})}
      </Button>

      {cartPaymentIcons?.length > 0 && (
        <Flex className="cart-pricing-summary__payment-icons" {...styles.paymentIcons}>
          {cartPaymentIcons.map((icon) => (
            <Box key={icon.sys?.id} className="cart-pricing-summary__payment-icon" {...styles.paymentIcon}>
              <ContentfulComponent component={icon} />
            </Box>
          ))}
        </Flex>
      )}
    </Stack>
  );
};

CartPricingSummary.displayName = 'CartPricingSummary';
CartPricingSummary.propTypes = {
  totalSavings: PropTypes.number,
};

export default CartPricingSummary;
